import React, { useEffect, useRef, useState } from 'react';

/**
 * Imperative confirm dialog — replacement for `window.confirm`.
 * Usage: `if (await confirm({ title: '...', danger: true })) { ... }`
 * Mount <ConfirmDialog /> once at the app root.
 */

export interface ConfirmOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

interface InternalState extends ConfirmOptions {
  resolve: (result: boolean) => void;
}

let openHandler: ((opts: ConfirmOptions) => Promise<boolean>) | null = null;

export function confirm(opts: ConfirmOptions): Promise<boolean> {
  if (openHandler) return openHandler(opts);
  if (typeof window !== 'undefined') {
    return Promise.resolve(window.confirm(`${opts.title}${opts.description ? `\n\n${opts.description}` : ''}`));
  }
  return Promise.resolve(false);
}

export function ConfirmDialog() {
  const [state, setState] = useState<InternalState | null>(null);
  const stateRef = useRef<InternalState | null>(null);
  stateRef.current = state;

  useEffect(() => {
    openHandler = (opts) =>
      new Promise<boolean>((resolve) => {
        setState({ ...opts, resolve });
      });
    return () => {
      openHandler = null;
    };
  }, []);

  const close = (result: boolean) => {
    const cur = stateRef.current;
    if (!cur) return;
    cur.resolve(result);
    setState(null);
  };

  if (!state) return null;

  return (
    <div
      onClick={() => close(false)}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: '#fff',
          borderRadius: 6,
          padding: 20,
          maxWidth: 320,
          width: '90%',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>{state.title}</h3>
        {state.description && (
          <p style={{ marginTop: 8, marginBottom: 0, color: '#475569', fontSize: 13, whiteSpace: 'pre-line' }}>
            {state.description}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 16 }}>
          <button
            onClick={() => close(false)}
            style={{
              padding: '6px 12px',
              borderRadius: 4,
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              cursor: 'pointer',
              fontSize: 12,
            }}
          >
            {state.cancelLabel ?? 'Cancel'}
          </button>
          <button
            onClick={() => close(true)}
            style={{
              padding: '6px 12px',
              borderRadius: 4,
              border: 'none',
              backgroundColor: state.danger ? '#dc2626' : '#2563eb',
              color: '#fff',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            {state.confirmLabel ?? 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
